import { format, formatDistanceToNow as fnsFormatDistanceToNow, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'

// Date formatting functions
export const formatDate = (date, pattern = 'dd/MM/yyyy HH:mm') => {
  if (!date) return '-'
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    return format(dateObj, pattern, { locale: tr })
  } catch (error) {
    console.error('Date formatting error:', error)
    return '-'
  }
}

export const formatDateShort = (date) => {
  return formatDate(date, 'dd/MM/yyyy')
}

export const formatDateTime = (date) => {
  return formatDate(date, 'dd/MM/yyyy HH:mm:ss')
}

export const formatDistanceToNow = (date) => {
  if (!date) return '-'
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    return fnsFormatDistanceToNow(dateObj, { 
      addSuffix: true, 
      locale: tr 
    })
  } catch (error) {
    console.error('Distance formatting error:', error)
    return '-'
  }
}

// Currency formatting functions
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-'
  
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch (error) {
    console.error('Currency formatting error:', error)
    return `${amount} ${currency}`
  }
}

export const formatCredits = (amount) => {
  if (amount === null || amount === undefined) return '0 kredi'
  
  try {
    const formatted = new Intl.NumberFormat('tr-TR').format(amount)
    return `${formatted} kredi`
  } catch (error) {
    console.error('Credits formatting error:', error)
    return `${amount} kredi`
  }
}

// Number formatting functions
export const formatNumber = (number, options = {}) => {
  if (number === null || number === undefined) return '-'
  
  try {
    return new Intl.NumberFormat('tr-TR', options).format(number)
  } catch (error) {
    console.error('Number formatting error:', error)
    return number.toString()
  }
}

export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '-'
  
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value / 100)
  } catch (error) {
    console.error('Percentage formatting error:', error)
    return `${value}%`
  }
}

// File size formatting
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  if (!bytes) return '-'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Phone number formatting
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '-'
  
  // Remove all non-digit characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '')
  
  // If it starts with +, format it as international
  if (cleaned.startsWith('+')) {
    return cleaned
  }
  
  // Otherwise, add + if it's a long enough number
  if (cleaned.length >= 10) {
    return `+${cleaned}`
  }
  
  return phoneNumber
}

// Status formatting
export const formatStatus = (status) => {
  const statusMap = {
    active: 'Aktif',
    inactive: 'Pasif',
    suspended: 'Askıda',
    banned: 'Yasaklı',
    pending: 'Beklemede',
    completed: 'Tamamlandı',
    failed: 'Başarısız',
    cancelled: 'İptal Edildi',
    expired: 'Süresi Doldu',
    sms_received: 'SMS Alındı',
  }
  
  return statusMap[status] || status || '-'
}

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '-'
  
  if (text.length <= maxLength) return text
  
  return text.substring(0, maxLength) + '...'
}

// Duration formatting
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '-'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  
  if (hours > 0) {
    return `${hours}s ${minutes}d ${remainingSeconds}s`
  } else if (minutes > 0) {
    return `${minutes}d ${remainingSeconds}s`
  } else {
    return `${remainingSeconds}s`
  }
}

// Capitalize first letter
export const capitalize = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// Format user role
export const formatUserRole = (role) => {
  const roleMap = {
    admin: 'Admin',
    super_admin: 'Süper Admin',
    user: 'Kullanıcı',
    moderator: 'Moderatör',
  }
  
  return roleMap[role] || capitalize(role) || 'Kullanıcı'
}